import React from "react";
import { useTranslation } from "react-i18next";
import {
  useEditAllocationWarning,
  useEditPlanzoneSettings,
  useRetrievePlanzoneSettings,
} from "@planzone/view-models";

import { Checkbox } from "@common/checkbox";
import { NumberInput } from "@common/numberInput";
import { Props } from "./types";
import styles from "./styles.scss";

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" />
    </svg>
  );
}

function PlanningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7v-5z" />
    </svg>
  );
}

function SystemIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z" />
    </svg>
  );
}

function PeopleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  );
}

function Settings({ planzone }: Props) {
  const { t } = useTranslation();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const { editPlanzoneSettings } = useEditPlanzoneSettings(planzone);
  const { editAllocationWarning } = useEditAllocationWarning(planzone);

  const [warningConfig, setWarningConfig] = React.useState({
    criticalDailyAmplitudeHours: planzone.criticalDailyAmplitudeHours,
    criticalDailyVolumeHours: planzone.criticalDailyVolumeHours,
    criticalRestPeriodHours: planzone.criticalRestPeriodHours,
  });

  function toggle(key: keyof typeof settings) {
    editPlanzoneSettings({
      planzone,
      settings,
      nextSettings: {
        ...settings,
        [key]: !settings[key],
      },
    });
  }

  function updateWarningConfig(key: keyof typeof warningConfig, value: number) {
    const nextConfig = { ...warningConfig, [key]: value };
    setWarningConfig(nextConfig);
    editAllocationWarning({ planzone, allocationWarningConfig: nextConfig });
  }

  return (
    <div className={styles.settings}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <ClockIcon className={styles.sectionIcon} />
          <p className={styles.sectionTitle}>
            {t("planzoneParametersPage.settings.sections.timeTracking")}
          </p>
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("areTimeSheetsAutoIntegrated")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.areTimeSheetsAutoIntegrated")}
          </p>
          <Checkbox isChecked={settings.areTimeSheetsAutoIntegrated} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("enterTimeAllocationInDays")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.enterTimeAllocationInDays")}
          </p>
          <Checkbox isChecked={settings.enterTimeAllocationInDays} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("lockBeforeIntegrationDate")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.lockBeforeIntegrationDate")}
          </p>
          <Checkbox isChecked={settings.lockBeforeIntegrationDate} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("showTaskCompletionPercentage")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.showTaskCompletionPercentage")}
          </p>
          <Checkbox isChecked={settings.showTaskCompletionPercentage} />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <PlanningIcon className={styles.sectionIcon} />
          <p className={styles.sectionTitle}>
            {t("planzoneParametersPage.settings.sections.planning")}
          </p>
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("conserveElementDuration")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.conserveElementDuration")}
          </p>
          <Checkbox isChecked={settings.conserveElementDuration} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("enableSelfAssignment")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.enableSelfAssignmentButton")}
          </p>
          <Checkbox isChecked={settings.enableSelfAssignment} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("syncAllocationDurationWithTask")}
        >
          <p className={styles.name}>
            {t(
              "planzoneParametersPage.settings.syncAllocationDurationWithTask",
            )}
          </p>
          <Checkbox isChecked={settings.syncAllocationDurationWithTask} />
        </div>
        <div className={styles.warningRow}>
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.warnOnDailyCriticalAmplitude")}
          </p>
          <div className={styles.warningControls}>
            <NumberInput
              value={warningConfig.criticalDailyAmplitudeHours ?? 0}
              input={(value) =>
                updateWarningConfig("criticalDailyAmplitudeHours", value)
              }
              min={0}
              step={0.5}
            />
            <div onClick={() => toggle("warnOnDailyCriticalAmplitude")}>
              <Checkbox isChecked={settings.warnOnDailyCriticalAmplitude} />
            </div>
          </div>
        </div>
        <div className={styles.warningRow}>
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.warnOnDailyCriticalVolume")}
          </p>
          <div className={styles.warningControls}>
            <NumberInput
              value={warningConfig.criticalDailyVolumeHours ?? 0}
              input={(value) =>
                updateWarningConfig("criticalDailyVolumeHours", value)
              }
              min={0}
              step={0.5}
            />
            <div onClick={() => toggle("warnOnDailyCriticalVolume")}>
              <Checkbox isChecked={settings.warnOnDailyCriticalVolume} />
            </div>
          </div>
        </div>
        <div className={styles.warningRow}>
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.warnOnInsufficientRestPeriod")}
          </p>
          <div className={styles.warningControls}>
            <NumberInput
              value={warningConfig.criticalRestPeriodHours ?? 0}
              input={(value) =>
                updateWarningConfig("criticalRestPeriodHours", value)
              }
              min={0}
              step={0.5}
            />
            <div onClick={() => toggle("warnOnInsufficientRestPeriod")}>
              <Checkbox isChecked={settings.warnOnInsufficientRestPeriod} />
            </div>
          </div>
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("warnOnUnavailability")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.warnOnUnavailability")}
          </p>
          <Checkbox isChecked={settings.warnOnUnavailability} />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <PeopleIcon className={styles.sectionIcon} />
          <p className={styles.sectionTitle}>
            {t(
              "planzoneParametersPage.settings.sections.collaboratorPermissions",
            )}
          </p>
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("canWorkersCreateTask")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.workersCanCreateTask")}
          </p>
          <Checkbox isChecked={settings.canWorkersCreateTask} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("canWorkersEditTask")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.workersCanEditTask")}
          </p>
          <Checkbox isChecked={settings.canWorkersEditTask} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("canWorkersRemoveTask")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.workersCanRemoveTask")}
          </p>
          <Checkbox isChecked={settings.canWorkersRemoveTask} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("canWorkersCreateList")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.workersCanCreateList")}
          </p>
          <Checkbox isChecked={settings.canWorkersCreateList} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("canWorkersEditList")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.workersCanEditList")}
          </p>
          <Checkbox isChecked={settings.canWorkersEditList} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("canWorkersRemoveList")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.workersCanRemoveList")}
          </p>
          <Checkbox isChecked={settings.canWorkersRemoveList} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("canWorkersCreateMilestone")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.workersCanCreateMilestone")}
          </p>
          <Checkbox isChecked={settings.canWorkersCreateMilestone} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("canWorkersEditMilestone")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.workersCanEditMilestone")}
          </p>
          <Checkbox isChecked={settings.canWorkersEditMilestone} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("canWorkersRemoveMilestone")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.workersCanRemoveMilestone")}
          </p>
          <Checkbox isChecked={settings.canWorkersRemoveMilestone} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("canWorkersCreateEvent")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.workersCanCreateEvent")}
          </p>
          <Checkbox isChecked={settings.canWorkersCreateEvent} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("canWorkersEditEvent")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.workersCanEditEvent")}
          </p>
          <Checkbox isChecked={settings.canWorkersEditEvent} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("canWorkersRemoveEvent")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.workersCanRemoveEvent")}
          </p>
          <Checkbox isChecked={settings.canWorkersRemoveEvent} />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <SystemIcon className={styles.sectionIcon} />
          <p className={styles.sectionTitle}>
            {t("planzoneParametersPage.settings.sections.system")}
          </p>
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("areUsersNotifiedAfterAccountCreation")}
        >
          <p className={styles.name}>
            {t(
              "planzoneParametersPage.settings.areUsersNotifiedAfterAccountCreation",
            )}
          </p>
          <Checkbox isChecked={settings.areUsersNotifiedAfterAccountCreation} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("allowDataScrapingForDebug")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.allowDataScrapingForDebug")}
          </p>
          <Checkbox isChecked={settings.allowDataScrapingForDebug} />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("areProjectStatsHiddenInProjectListing")}
        >
          <p className={styles.name}>
            {t(
              "planzoneParametersPage.settings.hideProjectStatsInProjectListing",
            )}
          </p>
          <Checkbox
            isChecked={settings.areProjectStatsHiddenInProjectListing}
          />
        </div>
        <div
          className={styles.row}
          onClick={() => toggle("areWeeklyCalendarLockedForNonAdmin")}
        >
          <p className={styles.name}>
            {t("planzoneParametersPage.settings.lockWeeklyCalendarForNonAdmin")}
          </p>
          <Checkbox isChecked={settings.areWeeklyCalendarLockedForNonAdmin} />
        </div>
      </div>
    </div>
  );
}

export { Settings };
